import React, { useState, useCallback, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./About.css";

// ─── \n → <br /> ────────────────────────────────────────────────────
function formatText(text) {
  if (typeof text !== "string") return text;
  return text.split("\n").map((line, i, arr) => (
    <React.Fragment key={i}>
      {line}
      {i < arr.length - 1 && <br />}
    </React.Fragment>
  ));
}

// ─── useInView ───────────────────────────────────────────────────────
function useInView(threshold = 0, rootMargin = "0px 0px -60px 0px") {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.unobserve(el);
        }
      },
      { threshold, rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);
  return [ref, inView];
}

// ─── 会社データ ──────────────────────────────────────────────────────
const COMPANY_DATA = [
  {
    num: "01", heading: "Company Profile", headingJp: "会社概要",
    details: [
      { title: "商号",     text: "株式会社ZENKYO", zenkyoLight: true },
      { title: "代表者",   text: "代表取締役 安藤 肇" },
      { title: "創業",     text: "1964年11月1日" },
      { title: "設立",     text: "1966年5月12日" },
      { title: "資本金",   text: "5,000万円" },
      { title: "法人番号", text: "8011501002435" },
    ],
  },
  {
    num: "02", heading: "Location & Contact", headingJp: "所在地・連絡先",
    details: [
      { title: "従業員数",   text: "70名（2025年9月末現在）" },
      { title: "本店所在地", text: "〒115-0051　東京都北区浮間3丁目19番19号" },
      { title: "事業所在地", text: "〒335-0027　埼玉県戸田市氷川町2丁目17番27号" },
      { title: "第二工場",   text: "〒335-0027　埼玉県戸田市氷川町2丁目17番26号" },
      { title: "TEL",        text: "048-444-9700（代表）" },
      { title: "FAX",        text: "048-444-9938" },
    ],
  },
  {
    num: "03", heading: "Business & License", headingJp: "事業内容・許認可",
    details: [
      { title: "事業内容", text: "半導体関連装置\n開発・設計・製造・販売" },
      { title: "主な製品", text: "ウェーハ洗浄装置・保管・移送装置など" },
      { title: "許認可",   text: "(機械器具設置工事業)\n埼玉県知事 第67932号" },
      { title: "認定", type: "image", label: "埼玉県受動喫煙防止対策実施施設等", image: "images/certification_20251219.jpg" },
      { title: "認定", type: "image", label: "全国健康保険協会埼玉支部健康優良企業", image: "images/certification_20260323.jpg" },
      { title: "認定", type: "image", label: "健康経営優良法人2026", image: "images/health_2026.jpg" },
    ],
  },
  {
    num: "04", heading: "Banking Partners", headingJp: "取引金融機関",
    details: [
      { title: "みずほ銀行",      text: "赤羽支店" },
      { title: "三菱ＵＦＪ銀行", text: "赤羽駅前支店" },
      { title: "三井住友銀行",    text: "赤羽支店" },
      { title: "武蔵野銀行",      text: "戸田西支店" },
      { title: "埼玉りそな銀行",  text: "戸田支店" },
      { title: "城北信用金庫",    text: "戸田支店" },
    ],
  },
];

// ─── FadeUp ─────────────────────────────────────────────────────────
function FadeUp({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity:         inView ? 1 : 0,
        transform:       inView ? "translateY(0)" : "translateY(32px)",
        transition:      `opacity 1s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 1s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

// ─── SlideIn (テキストを左から) ──────────────────────────────────────
function SlideIn({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity:    inView ? 1 : 0,
        transform:  inView ? "translateX(0)" : "translateX(-24px)",
        transition: `opacity 1.1s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 1.1s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

// ─── Section Label (WHO WE ARE 系) ───────────────────────────────────
function SectionLabel({ label }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className="ab2-label-wrap" style={{ opacity: inView ? 1 : 0, transition: "opacity 1.2s ease 0.1s" }}>
      <span className="ab2-label">{label}</span>
    </div>
  );
}

const MODAL_INITIAL = { show: false, image: "", title: "" };

export default function About() {
  const [modal, setModal] = useState(MODAL_INITIAL);
  const openModal  = useCallback((image, title) => setModal({ show: true, image, title }), []);
  const closeModal = useCallback(() => setModal(MODAL_INITIAL), []);

  const buildRows = (details) =>
    details.map((d) => ({
      title: d.title,
      content: d.type === "image" ? (
        <>
          <div>{d.label}</div>
          <span className="about-cert-wrapper">
            <img
              src={d.image}
              alt={d.label}
              className="about-cert-thumb"
              role="button"
              tabIndex={0}
              onClick={() => openModal(d.image, d.label)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openModal(d.image, d.label); }}
            />
          </span>
        </>
      ) : d.zenkyoLight ? (
        <span className="about-zenkyo-brand">
          株式会社<span className="zenkyo-light">ZENKYO</span>
        </span>
      ) : (
        formatText(d.text)
      ),
    }));

  return (
    <>
      <div id="page-top" />

      {/* ═══════════════════════════════════════════════════
          HERO — フルブリード
      ═══════════════════════════════════════════════════ */}
      <section className="ab2-hero">
        <div className="ab2-hero-bg" aria-hidden="true" />
        <div className="ab2-hero-content">
          <FadeUp delay={0.1}>
            <p className="ab2-hero-eyebrow">About Us</p>
          </FadeUp>
          <FadeUp delay={0.35}>
            <h1 className="ab2-hero-title" ><span className="zenkyo-light">ZENKYO</span></h1>
          </FadeUp>
          <FadeUp delay={0.55}>
            <p className="ab2-hero-sub">シリコンウェーハ洗浄装置のパイオニア</p>
          </FadeUp>
        </div>
        <div className="ab2-hero-scroll" aria-hidden="true">
          <span className="ab2-scroll-line" />
          <span className="ab2-scroll-text">SCROLL</span>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          IDENTITY ブロック（大きな装飾テキスト付き）
      ═══════════════════════════════════════════════════ */}
      <section className="ab2-identity">
        <div className="ab2-identity-inner">

          {/* ── 右ロゴ ── */}
          <div className="ab2-identity-logo-wrap" aria-hidden="true">
            <img src={`${process.env.PUBLIC_URL}/logonywmo.svg`} alt="" className="ab2-neu-logo" />
          </div>

          <div className="ab2-identity-body">
            <FadeUp delay={0}>
              <p className="ab2-identity-num">—&nbsp;00</p>
              <h2 className="ab2-identity-title">
                技術で未来を、<br />確かな形に
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="ab2-identity-lead">
                1964年の創業以来、株式会社<span className="zenkyo-light">ZENKYO</span>は半導体製造プロセスを支える製品を通じ、業界の進化を支えてきました。
                今までも、そしてこれからも技術の最前線で、お客様とともに未来をつくり続けています。
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          会社情報テーブル（全4セクション）
      ═══════════════════════════════════════════════════ */}
      <section className="ab2-data">
        <SectionLabel label="COMPANY DATA" />

        <div className="ab2-data-inner">

          {COMPANY_DATA.map((section, si) => {
            const rows = buildRows(section.details);
            return (
              <div key={section.num} className="ab2-data-section">
                {/* セクションヘッダー */}
                <SlideIn delay={0}>
                  <div className="ab2-data-header">
                    <span className="ab2-data-num">{section.num}</span>
                    <div className="ab2-data-titles">
                      <span className="ab2-data-en">{section.heading}</span>
                      <span className="ab2-data-jp">{section.headingJp}</span>
                    </div>
                  </div>
                </SlideIn>

                {/* テーブル行 */}
                <ul className="ab2-rows">
                  {rows.map((row, ri) => (
                    <FadeUp key={ri} delay={ri * 0.08}>
                      <li className="ab2-row">
                        <span className="ab2-row-title">{row.title}</span>
                        <span className="ab2-row-content">{row.content}</span>
                      </li>
                    </FadeUp>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          各種情報（組織・沿革・アクセス）
      ═══════════════════════════════════════════════════ */}
      <section className="ab2-info">
        <SectionLabel label="INFORMATION" />
        <div className="ab2-info-inner">
          {/* 組織図 */}
          <FadeUp delay={0}>
            <button
              className="ab2-info-card"
              onClick={() => openModal("images/organization.jpg", "組織図")}
            >
              <span className="ab2-card-icon" aria-hidden="true">◫</span>
              <span className="ab2-card-sub">Organization</span>
              <h3 className="ab2-card-title">組織図</h3>
              <span className="ab2-card-btn">VIEW →</span>
            </button>
          </FadeUp>

          {/* 会社沿革 */}
          <FadeUp delay={0.15}>
            <Link to="/history#page-top" className="ab2-info-card">
              <span className="ab2-card-icon" aria-hidden="true">◷</span>
              <span className="ab2-card-sub">History</span>
              <h3 className="ab2-card-title">会社沿革</h3>
              <span className="ab2-card-btn">VIEW →</span>
            </Link>
          </FadeUp>

          {/* アクセス */}
          <FadeUp delay={0.3}>
            <a
              href="https://www.google.com/maps?q=埼玉県戸田市氷川町2-17-27"
              target="_blank"
              rel="noopener noreferrer"
              className="ab2-info-card"
            >
              <span className="ab2-card-icon" aria-hidden="true">◎</span>
              <span className="ab2-card-sub">Access</span>
              <h3 className="ab2-card-title">アクセス</h3>
              <span className="ab2-card-btn">MAP →</span>
            </a>
          </FadeUp>
        </div>
      </section>

      {/* ─── モーダル ────────────────────────────────────── */}
      <Modal show={modal.show} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{modal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <img src={modal.image} alt={modal.title} style={{ maxWidth: "100%", height: "auto" }} />
        </Modal.Body>
      </Modal>
    </>
  );
}